import { useTranslate } from "ra-core";
import { useState } from "react";

import type { MonthRevenue } from "./pulseModel";
import { useHomeFormat } from "./useHomeFormat";

/** "Nice" axis maximum: 1, 2, 2.5 or 5 × a power of ten. */
const niceMax = (value: number) => {
  if (value <= 0) return 1;
  const power = Math.pow(10, Math.floor(Math.log10(value)));
  const step = [1, 2, 2.5, 5, 10].find((m) => m * power >= value) ?? 10;
  return step * power;
};

/** Five even steps: 1, 2, 2.5, 5 and 10 × 10ⁿ all divide into round ticks. */
const GRID_LINES = 5;

/**
 * «Ingresos»: invoiced vs collected per month (VAT excluded), last six
 * months. Grouped bars on one axis, a legend, a tooltip per month (hover or
 * keyboard focus) and a screen-reader table with the same numbers.
 */
export const RevenueChart = ({ rows }: { rows: MonthRevenue[] }) => {
  const translate = useTranslate();
  const format = useHomeFormat();
  const [active, setActive] = useState<number | null>(null);
  const t = (key: string, options?: Record<string, unknown>) =>
    translate(`desk.home.pulse.revenue.${key}`, options);
  const max = niceMax(
    Math.max(...rows.flatMap((row) => [row.invoiced, row.collected]), 0),
  );
  const totalInvoiced = rows.reduce((sum, row) => sum + row.invoiced, 0);
  const totalCollected = rows.reduce((sum, row) => sum + row.collected, 0);
  const pct = (value: number) => `${(value / max) * 100}%`;

  return (
    <section
      className="home-card home-revenue"
      aria-labelledby="home-revenue-title"
    >
      <header className="home-card__head">
        <h2 id="home-revenue-title" className="home-card__title">
          {t("title")}
          <span className="home-card__subtitle">{t("subtitle")}</span>
        </h2>
        <ul className="home-chart-legend" aria-label={t("legend")}>
          <li>
            <span data-series="invoiced" aria-hidden />
            {t("invoiced")}
          </li>
          <li>
            <span data-series="collected" aria-hidden />
            {t("collected")}
          </li>
        </ul>
      </header>
      <div className="home-revenue__totals">
        <p>
          <span className="home-revenue__big">
            {format.money(totalInvoiced)}
          </span>
          <span className="home-revenue__caption">{t("total_invoiced")}</span>
        </p>
        <p>
          <span className="home-revenue__big" data-series="collected">
            {format.money(totalCollected)}
          </span>
          <span className="home-revenue__caption">{t("total_collected")}</span>
        </p>
      </div>
      {totalInvoiced === 0 && totalCollected === 0 ? (
        <p className="home-agenda__empty">{t("empty")}</p>
      ) : (
        <div className="home-bars" aria-hidden={false}>
          <div className="home-bars__grid" aria-hidden>
            {Array.from({ length: GRID_LINES + 1 }, (_, index) => {
              const value = (max / GRID_LINES) * (GRID_LINES - index);
              return (
                <span key={index}>
                  <em>{format.compactMoney(value)}</em>
                </span>
              );
            })}
          </div>
          <div className="home-bars__plot">
            {rows.map((row, index) => {
              const month = format.monthShort(row.month);
              const label = t("month_label", {
                month: format.monthYear(row.month),
                invoiced: format.money(row.invoiced),
                collected: format.money(row.collected),
              });
              return (
                <div
                  key={row.key}
                  className="home-bars__col"
                  data-current={index === rows.length - 1 || undefined}
                  data-active={active === index || undefined}
                  tabIndex={0}
                  aria-label={label}
                  onMouseEnter={() => setActive(index)}
                  onMouseLeave={() => setActive(null)}
                  onFocus={() => setActive(index)}
                  onBlur={() => setActive(null)}
                >
                  <div className="home-bars__pair">
                    <span
                      data-series="invoiced"
                      style={{ height: pct(row.invoiced) }}
                    />
                    <span
                      data-series="collected"
                      style={{ height: pct(row.collected) }}
                    />
                  </div>
                  <span className="home-bars__label">{month}</span>
                  {active === index ? (
                    <span className="home-bars__tip" role="tooltip">
                      <strong>{format.monthYear(row.month)}</strong>
                      <span>
                        <i data-series="invoiced" aria-hidden />
                        {t("invoiced")}
                        <b>{format.money(row.invoiced)}</b>
                      </span>
                      <span>
                        <i data-series="collected" aria-hidden />
                        {t("collected")}
                        <b>{format.money(row.collected)}</b>
                      </span>
                    </span>
                  ) : null}
                </div>
              );
            })}
          </div>
        </div>
      )}
      <table className="sr-only">
        <caption>{t("title")}</caption>
        <thead>
          <tr>
            <th scope="col">{t("month")}</th>
            <th scope="col">{t("invoiced")}</th>
            <th scope="col">{t("collected")}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.key}>
              <th scope="row">{format.monthYear(row.month)}</th>
              <td>{format.money(row.invoiced)}</td>
              <td>{format.money(row.collected)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
};
